import { microSessionCatalog, MicroSession } from '@/data/microSessions';
import { progressTrackingService } from '@/services/progressTrackingService';
import { lessonPromptService } from '@/services/lessonPromptService';
import { getChatIntro } from '@/data/microSessionChatIntros';
import { ProgressStatus } from '@/types/progress';

const getTodayKey = (userId?: string) => {
  const today = new Date().toISOString().slice(0, 10);
  return `lyra-micro-daily-${userId || 'anon'}-${today}`;
};

const persistentStorage = {
  get: (key: string) => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  },
  set: (key: string, value: string) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  }
};

const markSessionStatus = async (
  sessionId: string,
  status: ProgressStatus,
  percentageComplete: number
) => {
  try {
    // Map status to LessonStatus type (the new system uses 'started' instead of 'not_started')
    let lessonStatus: 'started' | 'in_progress' | 'completed' = 'in_progress';
    if (status === 'completed') lessonStatus = 'completed';
    else if (status === 'not_started') lessonStatus = 'started';
    else if (status === 'in_progress') lessonStatus = 'in_progress';

    await progressTrackingService.updateProgress({
      lessonId: sessionId,
      status: lessonStatus,
      progressPercent: percentageComplete,
      // Don't send fake time - let actual elapsed time be calculated from timestamps
      // timeSpentSeconds was previously using fake formula: (percentageComplete / 100) * 600
      progressData: { source: 'micro-dashboard' }
    });
  } catch (error) {
    console.warn('[microSessionService] Failed to mark session status', {
      sessionId,
      status,
      error
    });
  }
};

export const microSessionService = {
  getCatalog(): MicroSession[] {
    return microSessionCatalog;
  },

  getSessionById(id: string): MicroSession | undefined {
    return microSessionCatalog.find((session) => session.id === id);
  },

  getDailyFocus(userId?: string): MicroSession {
    const key = getTodayKey(userId);
    const storedId = persistentStorage.get(key);

    if (storedId) {
      const storedSession = this.getSessionById(storedId);
      if (storedSession) {
        return storedSession;
      }
    }

    const eligible = microSessionCatalog.filter((session) => session.dailyEligible);
    const chosen = eligible[Math.floor(Math.random() * eligible.length)] || microSessionCatalog[0];
    persistentStorage.set(key, chosen.id);
    return chosen;
  },

  getContinueLearning(progress: Array<{ contentId: string; status: ProgressStatus; percentageComplete: number; completedAt?: string | null }>): MicroSession[] {
    // Only show sessions that are explicitly in_progress AND not completed
    // Bug fix: Check both status AND completedAt for consistency with MicroLessonWrapper
    // This handles edge cases where completedAt is set but status wasn't updated correctly
    const activeIds = progress
      .filter((item) => item.status === 'in_progress' && !item.completedAt)
      .map((item) => item.contentId);

    return microSessionCatalog.filter((session) => activeIds.includes(session.id));
  },

  getExploreSessions(limit = 3): MicroSession[] {
    return [...microSessionCatalog]
      .sort((a, b) => Date.parse(b.releasedAt) - Date.parse(a.releasedAt))
      .slice(0, limit);
  },

  getPlaylist(excludeIds: string[] = []): MicroSession[] {
    return microSessionCatalog.filter((session) => !excludeIds.includes(session.id));
  },

  startSession(sessionId: string) {
    return markSessionStatus(sessionId, 'in_progress', 40);
  },

  completeSession(sessionId: string) {
    return markSessionStatus(sessionId, 'completed', 100);
  },

  /**
   * Get session with database prompt overlay
   * Falls back to file-based prompts if no DB override exists
   */
  async getSessionWithPrompts(sessionId: string): Promise<MicroSession | null> {
    // Get base session from catalog (file-based)
    const session = this.getSessionById(sessionId);
    if (!session) return null;

    try {
      // Try to get DB prompt override
      const dbPrompt = await lessonPromptService.getPromptByLessonId(sessionId);

      // If no DB prompt, return session with file-based chat intro
      if (!dbPrompt) {
        return {
          ...session,
          // Ensure chat intro is available (from file)
          _chatIntro: getChatIntro(sessionId),
        } as MicroSession & { _chatIntro: string };
      }

      // Merge DB prompts over file-based defaults
      return {
        ...session,
        aiPrompt: dbPrompt.aiPrompt ?? session.aiPrompt,
        introPrompt: dbPrompt.introPrompt ?? session.introPrompt,
        promptGuidance: dbPrompt.promptGuidance ?? session.promptGuidance,
        narrative: dbPrompt.narrative ?? session.narrative,
        contextScenarios: dbPrompt.contextScenarios ?? session.contextScenarios,
        quickPrompts: dbPrompt.quickPrompts ?? session.quickPrompts,
        // Add chat intro from DB or fall back to file
        _chatIntro: dbPrompt.chatIntro ?? getChatIntro(sessionId),
      } as MicroSession & { _chatIntro: string };
    } catch (error) {
      console.warn('[microSessionService] Error fetching DB prompt, using file defaults:', error);
      // On error, return session with file-based prompts
      return {
        ...session,
        _chatIntro: getChatIntro(sessionId),
      } as MicroSession & { _chatIntro: string };
    }
  }
};
